import type { ReactNode } from "react";
import { cn } from "@/components/ui/cn";

type VRadioOption<T extends string> = {
  value: T;
  label: ReactNode;
};

type VRadioGroupProps<T extends string> = {
  name: string;
  value?: T;
  options: ReadonlyArray<VRadioOption<T>>;
  onChange?: (value: T) => void;
  orientation?: "vertical" | "horizontal";
  className?: string;
};

export function VRadioGroup<T extends string>({
  name,
  value,
  options,
  onChange,
  orientation = "vertical",
  className,
}: VRadioGroupProps<T>): React.JSX.Element {
  return (
    <div
      role="radiogroup"
      className={cn(
        "flex gap-3",
        orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
        className,
      )}
    >
      {options.map((opt) => {
        const checked = value === opt.value;
        return (
          <label
            key={opt.value}
            className={cn(
              "inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors",
              checked
                ? "border-primary-600 bg-primary-50 text-primary-800"
                : "border-input bg-card hover:bg-muted",
            )}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => onChange?.(opt.value)}
              className="size-4 accent-primary-600"
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export type { VRadioGroupProps, VRadioOption };
